import { useState, useEffect, useCallback, useMemo } from "react";
import { Button, InputBase, SvgIcon, Tab, Tabs } from "@mui/material";

import "./style.scss";
import { ReactComponent as UserIcon } from "../../../assets/user.icon.svg";
import { ReactComponent as EditIcon } from "../../../assets/edit.icon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/trash.icon.svg";
import { ReactComponent as LinkIcon } from "../../../assets/link.icon.svg";
import { ReactComponent as FingerprintIcon } from "../../../assets/fingerprint.icon.svg";
import { ReactComponent as RefreshIcon } from "../../../assets/refresh.icon.svg";
import { ReactComponent as MbaPlaceholder } from "../../../assets/placeholder-mba.svg";
import {
    UserWithKBRoles,
    InvitedUserWithKB,
    GroupedInvitedUser
} from "../../../common/interfaces/admin/user-with-kb-roles.interface";
import {
    fetchUsersWithKBRoles,
    fetchInvitedUsersWithKB,
    addUsersToKBs,
    removeUsersFromKBs,
    changeKBRoles,
    resendInvitations,
    deleteInvitations,
    deleteUsers
} from "../../../api/admin/user-management/endpoints-referrers";
import { fetchKnowledgeBases } from "../../../api/admin/knowledge-base/endpoints-referrers";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { LoaderView } from "../../../components/loader/loader.comp";
import { ActiveUsersTable } from "../../../components/admin/user-management/active-users-table.comp";
import { InvitedUsersTable } from "../../../components/admin/user-management/invited-users-table.comp";
import { AddUsersModal } from "../../../components/admin/user-management/add-users-modal.comp";
import { BulkChangeRoleModal } from "../../../components/admin/user-management/bulk-change-role-modal.comp";
import { BulkRemoveModal } from "../../../components/admin/user-management/bulk-remove-modal.comp";
import {
    ActionMenu,
    CheckboxMenuFilterT,
    DropdownMenuType
} from "../../../components/menu-filter/menu-filter.comp";
import { MenuItem } from "../../../common/interfaces/comps/menu-item.interface";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../../components/confirmation-popup/confirmation-popup.comp";
import HeaderLayout from "../../../components/layout/header-layout.comp";
import { KBOption } from "../../../components/admin/user-management/kb-multi-select.comp";

enum TabValue {
    activeUsers = 0,
    invitedUsers = 1
}

const UserManagementView = () => {
    const { user } = FirebaseAuthContainer.useContainer();

    // Data state
    const [activeUsers, setActiveUsers] = useState<UserWithKBRoles[]>([]);
    const [invitedUsers, setInvitedUsers] = useState<GroupedInvitedUser[]>([]);
    const [activeCount, setActiveCount] = useState(0);
    const [invitedCount, setInvitedCount] = useState(0);
    const [allKBs, setAllKBs] = useState<KBOption[]>([]);

    // UI state
    const [currentTab, setCurrentTab] = useState<TabValue>(TabValue.activeUsers);
    const [isLoading, setIsLoading] = useState(true);
    const [emailSearch, setEmailSearch] = useState("");
    const [checkedRoleValues, setCheckedRoleValues] = useState<string[]>([]);
    const [checkedKBValues, setCheckedKBValues] = useState<string[]>([]);

    // Selection state
    const [checkedActiveUsers, setCheckedActiveUsers] = useState<UserWithKBRoles[]>([]);
    const [checkedInvitedUsers, setCheckedInvitedUsers] = useState<GroupedInvitedUser[]>([]);

    // Modal state
    const [addUsersModalOpen, setAddUsersModalOpen] = useState(false);
    const [changeRoleModalOpen, setChangeRoleModalOpen] = useState(false);
    const [removeModalOpen, setRemoveModalOpen] = useState(false);
    const [resendConfirmationOpen, setResendConfirmationOpen] = useState(false);
    const [deleteInvitesConfirmationOpen, setDeleteInvitesConfirmationOpen] = useState(false);
    const [deleteUsersConfirmationOpen, setDeleteUsersConfirmationOpen] = useState(false);
    const [pendingDeleteUserUid, setPendingDeleteUserUid] = useState<string | null>(null);

    const buildQuery = useCallback(() => {
        const params = new URLSearchParams();
        params.set("pageSize", "1000");
        if (emailSearch) params.set("search", emailSearch);
        checkedRoleValues.forEach(v => params.append("role", v));
        checkedKBValues.forEach(v => params.append("knowledgeBaseUid", v));
        return `?${params.toString()}`;
    }, [emailSearch, checkedRoleValues, checkedKBValues]);

    const loadActiveUsers = useCallback(() => {
        if (!user?.companyId) return Promise.resolve();
        return fetchUsersWithKBRoles(user.companyId, buildQuery()).then(response => {
            const { data } = response;
            setActiveUsers(data.items);
            setActiveCount(data.count);
        });
    }, [user, buildQuery]);

    const groupInvitedUsers = (items: InvitedUserWithKB[]): GroupedInvitedUser[] => {
        const grouped = new Map<string, GroupedInvitedUser>();
        for (const item of items) {
            const existing = grouped.get(item.email);
            if (existing) {
                existing.inviteIds.push(item.id);
                existing.knowledgeBases.push({
                    knowledgeBaseUid: item.knowledgeBaseUid,
                    knowledgeBaseDisplayName: item.knowledgeBaseDisplayName,
                    role: item.role
                });
                if (item.sentAt > existing.latestSentAt) {
                    existing.latestSentAt = item.sentAt;
                }
            } else {
                grouped.set(item.email, {
                    email: item.email,
                    inviteIds: [item.id],
                    knowledgeBases: [
                        {
                            knowledgeBaseUid: item.knowledgeBaseUid,
                            knowledgeBaseDisplayName: item.knowledgeBaseDisplayName,
                            role: item.role
                        }
                    ],
                    latestSentAt: item.sentAt
                });
            }
        }
        return Array.from(grouped.values());
    };

    const loadInvitedUsers = useCallback(() => {
        if (!user?.companyId) return Promise.resolve();
        return fetchInvitedUsersWithKB(user.companyId, buildQuery()).then(response => {
            const { data } = response;
            const grouped = groupInvitedUsers(data.items);
            setInvitedUsers(grouped);
            setInvitedCount(grouped.length);
        });
    }, [user, buildQuery]);

    const loadAllKBs = useCallback(() => {
        if (!user?.companyId) return;
        fetchKnowledgeBases(user.companyId).then(response => {
            const kbs = response.data.items.map((kb: any) => ({
                uid: kb.uid,
                displayName: kb.displayName
            }));
            setAllKBs(kbs);
        });
    }, [user]);

    const loadData = useCallback(() => {
        setIsLoading(true);
        Promise.all([loadActiveUsers(), loadInvitedUsers()]).finally(() => setIsLoading(false));
    }, [loadActiveUsers, loadInvitedUsers]);

    useEffect(() => {
        loadAllKBs();
    }, [loadAllKBs]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Active user actions
    const handleAddUsersAction = (emails: string[], kbs: KBOption[], role: string) => {
        if (!user?.companyId) return;
        setAddUsersModalOpen(false);
        setIsLoading(true);
        addUsersToKBs(user.companyId, {
            emails,
            knowledgeBaseUids: kbs.map(kb => kb.uid),
            role
        }).then(() => loadData());
    };

    const handleChangeRoleAction = (kbs: KBOption[], role: string) => {
        if (!user?.companyId) return;
        setChangeRoleModalOpen(false);
        setIsLoading(true);
        changeKBRoles(user.companyId, {
            userUids: checkedActiveUsers.map(u => u.uid),
            knowledgeBaseUids: kbs.map(kb => kb.uid),
            role
        }).then(() => {
            setCheckedActiveUsers([]);
            loadData();
        });
    };

    const handleRemoveAction = (kbs: KBOption[]) => {
        if (!user?.companyId) return;
        setRemoveModalOpen(false);
        setIsLoading(true);
        removeUsersFromKBs(user.companyId, {
            userUids: checkedActiveUsers.map(u => u.uid),
            knowledgeBaseUids: kbs.map(kb => kb.uid)
        }).then(() => {
            setCheckedActiveUsers([]);
            loadData();
        });
    };

    // Single-row actions
    const handleSingleChangeRole = (userUid: string, kbUid: string, newRole: string) => {
        if (!user?.companyId) return;
        changeKBRoles(user.companyId, {
            userUids: [userUid],
            knowledgeBaseUids: [kbUid],
            role: newRole
        }).then(() => {
            loadData();
        });
    };

    const handleSingleRemoveFromKB = (userUid: string, kbUid: string) => {
        if (!user?.companyId) return;
        removeUsersFromKBs(user.companyId, {
            userUids: [userUid],
            knowledgeBaseUids: [kbUid]
        }).then(() => {
            loadData();
        });
    };

    const handleDeleteSingle = (userUid: string) => {
        setPendingDeleteUserUid(userUid);
        setDeleteUsersConfirmationOpen(true);
    };

    const handleResendInviteForActiveUser = (userUid: string) => {
        if (!user?.companyId) return;
        resendInvitations(user.companyId, { userUids: [userUid] }).then(() => {
            loadData();
        });
    };

    const handleDeleteUsersBulk = () => {
        if (!user?.companyId) return;
        setDeleteUsersConfirmationOpen(false);

        const uidsToDelete = pendingDeleteUserUid
            ? [pendingDeleteUserUid]
            : checkedActiveUsers.map(u => u.uid);

        setPendingDeleteUserUid(null);
        setIsLoading(true);
        deleteUsers(user.companyId, { userUids: uidsToDelete }).then(() => {
            setCheckedActiveUsers([]);
            loadData();
        });
    };

    // Invited user actions
    const handleResendSingle = (item: GroupedInvitedUser) => {
        if (!user?.companyId) return;
        resendInvitations(user.companyId, { inviteIds: item.inviteIds }).then(() => {
            loadData();
        });
    };

    const handleResendBulk = () => {
        if (!user?.companyId) return;
        setResendConfirmationOpen(false);
        setIsLoading(true);
        resendInvitations(user.companyId, {
            inviteIds: checkedInvitedUsers.flatMap(u => u.inviteIds)
        }).then(() => {
            setCheckedInvitedUsers([]);
            loadData();
        });
    };

    const handleDeleteInvitesBulk = () => {
        if (!user?.companyId) return;
        setDeleteInvitesConfirmationOpen(false);
        setIsLoading(true);
        deleteInvitations(user.companyId, {
            inviteIds: checkedInvitedUsers.flatMap(u => u.inviteIds)
        }).then(() => {
            setCheckedInvitedUsers([]);
            loadData();
        });
    };

    const activeUserActions = [
        {
            icon: EditIcon,
            name: "Change Role...",
            action: () => setChangeRoleModalOpen(true)
        },
        {
            icon: TrashIcon,
            name: "Remove from Assistants...",
            action: () => setRemoveModalOpen(true)
        },
        {
            icon: TrashIcon,
            name: "Delete Users",
            action: () => {
                setPendingDeleteUserUid(null);
                setDeleteUsersConfirmationOpen(true);
            }
        }
    ];

    const invitedUserActions = [
        {
            icon: LinkIcon,
            name: "Resend Invites",
            action: () => setResendConfirmationOpen(true)
        },
        {
            icon: TrashIcon,
            name: "Delete Invites",
            action: () => setDeleteInvitesConfirmationOpen(true)
        }
    ];

    const roleMenuItems: MenuItem<string>[] = useMemo(
        () => [
            { label: "User", value: "user" },
            { label: "Manager", value: "manager" }
        ],
        []
    );

    const kbMenuItems: MenuItem<string>[] = useMemo(
        () => allKBs.map(kb => ({ label: kb.displayName, value: kb.uid })),
        [allKBs]
    );

    const checkedRoles = useMemo(
        () => roleMenuItems.filter(item => checkedRoleValues.includes(item.value)),
        [roleMenuItems, checkedRoleValues]
    );

    const checkedKBs = useMemo(
        () => kbMenuItems.filter(item => checkedKBValues.includes(item.value)),
        [kbMenuItems, checkedKBValues]
    );

    // Assistants assigned to at least one selected user — the only ones the
    // Remove-from-Assistants dropdown offers (you can't remove a user from an
    // assistant they aren't on).
    const removeModalKBs: KBOption[] = useMemo(() => {
        const kbUidSet = new Set<string>();
        for (const user of checkedActiveUsers) {
            for (const kb of user.knowledgeBases) {
                kbUidSet.add(kb.knowledgeBaseUid);
            }
        }
        return allKBs.filter(kb => kbUidSet.has(kb.uid));
    }, [checkedActiveUsers, allKBs]);

    const handleSetCheckedRoles = useCallback(
        (items: MenuItem<string>[]) => setCheckedRoleValues(items.map(i => i.value)),
        []
    );

    const handleSetCheckedKBs = useCallback(
        (items: MenuItem<string>[]) => setCheckedKBValues(items.map(i => i.value)),
        []
    );

    const buildFilterLabel = (defaultLabel: string, checked: MenuItem<string>[]) => {
        if (checked.length === 0) return defaultLabel;
        return checked.map(item => item.label).join(", ");
    };

    return (
        <HeaderLayout
            subheaderHeight={46}
            subheader={
                <div className="um-header">
                    <div className="um-header-title">
                        <span className="um-header-title__text" role="heading" aria-level={1}>
                            User Management
                        </span>
                    </div>
                    <div className="um-header-action">
                        <Button
                            className="um-header-action__button primary-button--icon-first"
                            variant="text"
                            startIcon={
                                <SvgIcon
                                    className="primary-button--icon-first__icon"
                                    component={UserIcon}
                                    inheritViewBox
                                />
                            }
                            onClick={() => setAddUsersModalOpen(true)}
                        >
                            <span className="primary-button--icon-first__text">Add Users</span>
                        </Button>
                    </div>
                </div>
            }
        >
            <div className="um-view">
                <div className="um-tabs">
                    <Tabs
                        value={currentTab}
                        onChange={(_e, newValue) => {
                            setCurrentTab(newValue);
                            setCheckedActiveUsers([]);
                            setCheckedInvitedUsers([]);
                        }}
                        className="um-tabs__tabs"
                    >
                        <Tab label={`Active Users (${activeCount})`} className="um-tabs__tab" />
                        <Tab label={`Invited Users (${invitedCount})`} className="um-tabs__tab" />
                    </Tabs>
                    <Button
                        className="um-tabs__refresh"
                        onClick={loadData}
                        size="small"
                        endIcon={<SvgIcon component={RefreshIcon} inheritViewBox />}
                    >
                        Refresh
                    </Button>
                </div>

                <div className="um-filters">
                    <div className="um-filters-action">
                        <ActionMenu
                            label="Action"
                            disabled={
                                currentTab === TabValue.activeUsers
                                    ? checkedActiveUsers.length === 0
                                    : checkedInvitedUsers.length === 0
                            }
                            actions={
                                currentTab === TabValue.activeUsers
                                    ? activeUserActions
                                    : invitedUserActions
                            }
                            minWidth={183}
                            dropdownMenuType={DropdownMenuType.wider}
                        />
                    </div>
                    <div className="um-filters-center">
                        <div className="um-filters-role">
                            <CheckboxMenuFilterT
                                label={buildFilterLabel("All Roles", checkedRoles)}
                                list={roleMenuItems}
                                checkedList={checkedRoles}
                                setCheckedList={handleSetCheckedRoles}
                                rightPosition={true}
                                minWidth={140}
                                onClear={() => setCheckedRoleValues([])}
                            />
                        </div>
                        <div className="um-filters-kb">
                            <CheckboxMenuFilterT
                                label={buildFilterLabel("All Assistants", checkedKBs)}
                                list={kbMenuItems}
                                checkedList={checkedKBs}
                                setCheckedList={handleSetCheckedKBs}
                                rightPosition={true}
                                minWidth={200}
                                onClear={() => setCheckedKBValues([])}
                            />
                        </div>
                    </div>
                    <div className="um-filters-search">
                        <InputBase
                            className="um-filters-search__input"
                            placeholder="Search by email..."
                            inputProps={{ "aria-label": "Search by email" }}
                            value={emailSearch}
                            onChange={e => setEmailSearch(e.target.value)}
                        />
                    </div>
                </div>

                <div className="um-content">
                    {currentTab === TabValue.activeUsers && (
                        <>
                            {activeUsers.length > 0 ? (
                                <ActiveUsersTable
                                    list={activeUsers}
                                    checkedItems={checkedActiveUsers}
                                    setCheckedItems={setCheckedActiveUsers}
                                    handleChangeRole={handleSingleChangeRole}
                                    handleRemoveFromKB={handleSingleRemoveFromKB}
                                    handleDeleteUser={handleDeleteSingle}
                                    handleResendInvite={handleResendInviteForActiveUser}
                                />
                            ) : (
                                !isLoading && (
                                    <div className="um-placeholder">
                                        <SvgIcon
                                            component={MbaPlaceholder}
                                            className="um-placeholder-logo"
                                            inheritViewBox
                                        />
                                    </div>
                                )
                            )}
                        </>
                    )}
                    {currentTab === TabValue.invitedUsers && (
                        <>
                            {invitedUsers.length > 0 ? (
                                <InvitedUsersTable
                                    list={invitedUsers}
                                    checkedItems={checkedInvitedUsers}
                                    setCheckedItems={setCheckedInvitedUsers}
                                    handleResend={handleResendSingle}
                                />
                            ) : (
                                !isLoading && (
                                    <div className="um-placeholder">
                                        <SvgIcon
                                            component={MbaPlaceholder}
                                            className="um-placeholder-logo"
                                            inheritViewBox
                                        />
                                    </div>
                                )
                            )}
                        </>
                    )}
                    <LoaderView show={isLoading} fullScreen={false} />
                </div>
            </div>

            <AddUsersModal
                open={addUsersModalOpen}
                knowledgeBases={allKBs}
                initialEmails={
                    currentTab === TabValue.activeUsers
                        ? checkedActiveUsers.map(u => u.email)
                        : checkedInvitedUsers.map(u => u.email)
                }
                handleClose={() => setAddUsersModalOpen(false)}
                handleAction={handleAddUsersAction}
            />
            <BulkChangeRoleModal
                open={changeRoleModalOpen}
                knowledgeBases={allKBs}
                handleClose={() => setChangeRoleModalOpen(false)}
                handleAction={handleChangeRoleAction}
            />
            <BulkRemoveModal
                open={removeModalOpen}
                knowledgeBases={removeModalKBs}
                handleClose={() => setRemoveModalOpen(false)}
                handleAction={handleRemoveAction}
            />
            <ConfirmationPopup
                open={resendConfirmationOpen}
                type={ConfirmationPopupType.default}
                icon={FingerprintIcon}
                title="Resend Invitations"
                description={`Resend invitation emails to ${checkedInvitedUsers.length} selected user(s)?`}
                actionName="Resend"
                handleAction={handleResendBulk}
                handleClose={() => setResendConfirmationOpen(false)}
            />
            <ConfirmationPopup
                open={deleteInvitesConfirmationOpen}
                type={ConfirmationPopupType.dangerous}
                icon={TrashIcon}
                title="Delete Invitations"
                description={`Are you sure you want to delete ${checkedInvitedUsers.length} invitation(s)?`}
                actionName="Delete"
                handleAction={handleDeleteInvitesBulk}
                handleClose={() => setDeleteInvitesConfirmationOpen(false)}
            />
            <ConfirmationPopup
                open={deleteUsersConfirmationOpen}
                type={ConfirmationPopupType.dangerous}
                icon={TrashIcon}
                title="Delete Users"
                description={
                    pendingDeleteUserUid
                        ? "Are you sure you want to permanently delete this user and all their data?"
                        : `Are you sure you want to permanently delete ${checkedActiveUsers.length} user(s) and all their data?`
                }
                actionName="Delete"
                handleAction={handleDeleteUsersBulk}
                handleClose={() => {
                    setDeleteUsersConfirmationOpen(false);
                    setPendingDeleteUserUid(null);
                }}
            />
        </HeaderLayout>
    );
};

export default UserManagementView;
